import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiSearch, FiEye, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import { getUsers, getUser, getUserScans, getUserLedger, toggleUser } from '../services/api';

const Users: React.FC = () => {
  const [users, setUsersList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [userType, setUserType] = useState('');
  const [showDetail, setShowDetail] = useState<any>(null);
  const [detailTab, setDetailTab] = useState('info');
  const [scans, setScans] = useState<any[]>([]);
  const [ledger, setLedger] = useState<any[]>([]);
  const [ledgerFilters, setLedgerFilters] = useState({
    search: '',
    transactionType: '',
    referenceType: '',
    dateFrom: '',
    dateTo: '',
  });

  useEffect(() => { loadUsers(); }, [page, userType]);

  const loadUsers = async () => {
    try {
      const res = await getUsers({ page, pageSize: 20, search, userType });
      setUsersList(res.data.data);
      setTotal(res.data.totalCount);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSearch = () => { setPage(1); loadUsers(); };

  const viewUser = async (id: number) => {
    try {
      const [userRes, scansRes, ledgerRes] = await Promise.all([
        getUser(id), getUserScans(id), getUserLedger(id, {})
      ]);
      setShowDetail(userRes.data.data);
      setScans(scansRes.data.data);
      setLedger(ledgerRes.data.data);
      setLedgerFilters({
        search: '',
        transactionType: '',
        referenceType: '',
        dateFrom: '',
        dateTo: '',
      });
      setDetailTab('info');
    } catch (err) { console.error(err); }
  };

  const loadLedger = async (userId: number, overrides: any = {}) => {
    try {
      const params = { ...ledgerFilters, ...overrides };
      const res = await getUserLedger(userId, params);
      setLedger(res.data.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load wallet ledger');
    }
  };

  const handleToggle = async (id: number) => {
    try {
      await toggleUser(id);
      toast.success('User status toggled');
      loadUsers();
    } catch (err) { toast.error('Failed'); }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div>
      <div className="flex-between mb-20">
        <div className="search-bar">
          <input className="form-control" placeholder="Search users..." value={search}
            onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
          <select className="form-control" style={{ maxWidth: 150 }} value={userType}
            onChange={(e) => { setUserType(e.target.value); setPage(1); }}>
            <option value="">All Types</option>
            <option value="labourer">Labourer</option>
            <option value="dealer">Dealer</option>
            <option value="contractor">Contractor</option>
          </select>
          <button className="btn btn-outline" onClick={handleSearch}><FiSearch /></button>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="loading">Loading...</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr><th>ID</th><th>Name</th><th>Mobile</th><th>Type</th><th>City</th>
                  <th>Balance</th><th>Scans</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.userId}>
                    <td>{u.userId}</td>
                    <td><strong>{u.fullName}</strong></td>
                    <td>{u.mobile}</td>
                    <td><span className="badge badge-info">{u.userType}</span></td>
                    <td>{u.city || '-'}</td>
                    <td>{u.userType === 'labourer' ? '-' : u.balance}</td>
                    <td>{u.scanCount}</td>
                    <td><span className={`badge ${u.isActive ? 'badge-success' : 'badge-danger'}`}>{u.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div className="flex gap-10">
                        <button className="btn btn-sm btn-outline" onClick={() => viewUser(u.userId)}><FiEye /></button>
                        <button className="btn btn-sm btn-outline" onClick={() => handleToggle(u.userId)}>
                          {u.isActive ? <FiToggleRight /> : <FiToggleLeft />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={9} className="text-center">No users found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>

      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(null)}>
          <div className="modal" style={{ minWidth: 700 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showDetail.fullName}</h3>
              <button className="modal-close" onClick={() => setShowDetail(null)}>&times;</button>
            </div>

            <div className="flex gap-10 mb-20">
              <button className={`btn btn-sm ${detailTab === 'info' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setDetailTab('info')}>Info</button>
              <button className={`btn btn-sm ${detailTab === 'scans' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setDetailTab('scans')}>Scans</button>
              {showDetail.userType !== 'labourer' && (
                <button className={`btn btn-sm ${detailTab === 'ledger' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setDetailTab('ledger')}>Wallet Ledger</button>
              )}
            </div>

            {detailTab === 'info' && (
              <div className="form-row">
                <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>Type</label><p>{showDetail.userType}</p></div>
                <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>Mobile</label><p>{showDetail.mobile}</p></div>
                <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>Email</label><p>{showDetail.email || '-'}</p></div>
                <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>City</label><p>{showDetail.city || '-'}</p></div>
                <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>State</label><p>{showDetail.state || '-'}</p></div>
                <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>UPI ID</label><p>{showDetail.upiId || '-'}</p></div>
                {showDetail.userType !== 'labourer' && (
                  <>
                    <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>Balance</label><p><strong>{showDetail.balance} pts</strong></p></div>
                    <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>Total Earned</label><p>{showDetail.totalEarned} pts</p></div>
                    <div><label style={{ fontWeight: 600, fontSize: 12, color: '#666' }}>Total Redeemed</label><p>{showDetail.totalRedeemed} pts</p></div>
                  </>
                )}
              </div>
            )}

            {detailTab === 'scans' && (
              <div className="table-container" style={{ maxHeight: 400, overflowY: 'auto' }}>
                <table>
                  <thead><tr><th>Product</th><th>Reward</th><th>Status</th><th>Date</th></tr></thead>
                  <tbody>
                    {scans.map((s: any) => (
                      <tr key={s.scanId}>
                        <td>{s.productName}</td>
                        <td>{s.rewardType === 'cash' ? `₹${s.rewardValue}` : `${s.rewardValue} pts`}</td>
                        <td><span className={`badge ${s.scanStatus === 'success' ? 'badge-success' : 'badge-danger'}`}>{s.scanStatus}</span></td>
                        <td>{new Date(s.scannedAt).toLocaleString()}</td>
                      </tr>
                    ))}
                    {scans.length === 0 && <tr><td colSpan={4} className="text-center">No scans</td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            {detailTab === 'ledger' && (
              <>
                <div className="card" style={{ marginBottom: 16, padding: 16 }}>
                  <div className="search-filters" style={{ minWidth: 'auto' }}>
                    <div className="search-box">
                      <FiSearch className="search-icon" />
                      <input
                        className="search-input"
                        placeholder="Search description, ref type or points..."
                        value={ledgerFilters.search}
                        onChange={(e) => setLedgerFilters({ ...ledgerFilters, search: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && showDetail && loadLedger(showDetail.userId)}
                      />
                    </div>
                    <select
                      className="filter-dropdown"
                      value={ledgerFilters.transactionType}
                      onChange={(e) => setLedgerFilters({ ...ledgerFilters, transactionType: e.target.value })}
                    >
                      <option value="">All Types</option>
                      <option value="credit">Credit</option>
                      <option value="debit">Debit</option>
                    </select>
                    <select
                      className="filter-dropdown"
                      value={ledgerFilters.referenceType}
                      onChange={(e) => setLedgerFilters({ ...ledgerFilters, referenceType: e.target.value })}
                    >
                      <option value="">All References</option>
                      <option value="qr_scan">QR Scan</option>
                      <option value="redemption">Redemption</option>
                      <option value="refund">Refund</option>
                      <option value="adjustment">Adjustment</option>
                    </select>
                    <input
                      className="form-control"
                      type="date"
                      value={ledgerFilters.dateFrom}
                      onChange={(e) => setLedgerFilters({ ...ledgerFilters, dateFrom: e.target.value })}
                    />
                    <input
                      className="form-control"
                      type="date"
                      value={ledgerFilters.dateTo}
                      onChange={(e) => setLedgerFilters({ ...ledgerFilters, dateTo: e.target.value })}
                    />
                    <button className="btn btn-outline" onClick={() => showDetail && loadLedger(showDetail.userId)}>
                      <FiSearch /> Filter
                    </button>
                  </div>
                </div>

                <div className="table-container" style={{ maxHeight: 400, overflowY: 'auto' }}>
                  <table>
                    <thead><tr><th>Type</th><th>Points</th><th>Balance</th><th>Ref</th><th>Description</th><th>Date</th></tr></thead>
                    <tbody>
                      {ledger.map((l: any) => (
                        <tr key={l.ledgerId}>
                          <td><span className={`badge ${l.transactionType === 'credit' ? 'badge-success' : 'badge-danger'}`}>{l.transactionType}</span></td>
                          <td>{l.transactionType === 'credit' ? '+' : '-'}{l.points}</td>
                          <td>{l.balanceAfter}</td>
                          <td>{l.referenceType}</td>
                          <td>{l.description || '-'}</td>
                          <td>{new Date(l.createdAt).toLocaleString()}</td>
                        </tr>
                      ))}
                      {ledger.length === 0 && <tr><td colSpan={6} className="text-center">No transactions</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
